import React, { useState, useEffect } from 'react';
import { ArrowRight, AlertCircle, QrCode, X, Search, Phone, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Profile } from '../lib/supabase';
import { Tooltip } from '../components/Tooltip';
import QrReader from 'react-qr-scanner';

const countryCodes = [
  { code: '+967', country: 'اليمن' },
  { code: '+249', country: 'السودان' },
  { code: '+20', country: 'مصر' },
  { code: '+966', country: 'السعودية' },
];

interface CustomerSearchResult {
  id: string;
  username: string;
  phone: string;
  country_code: string;
  smart_key: string;
  account_type: string;
}

interface InvitationData {
  phone: string;
  countryCode: string;
  address: string;
  shopOwnerName: string;
  shopName: string;
}

function AddCustomer() {
  const navigate = useNavigate();
  const [customerSmartKey, setCustomerSmartKey] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState('+967');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [vendorProfile, setVendorProfile] = useState<Profile | null>(null);
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState<CustomerSearchResult | null>(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationPhone, setInvitationPhone] = useState('');
  const [invitationCountryCode, setInvitationCountryCode] = useState('+967');
  const [invitationAddress, setInvitationAddress] = useState('');
  const [creatingInvitation, setCreatingInvitation] = useState(false);

  useEffect(() => {
    fetchVendorProfile();
  }, []);

  const fetchVendorProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('No profile found');
      if (profile.account_type !== 'shop_owner') {
        navigate('/home');
        return;
      }

      setVendorProfile(profile);
    } catch (err) {
      console.error('Error fetching vendor profile:', err);
      navigate('/home');
    }
  };

  const handlePhoneSearch = async () => {
    if (!phoneSearch.trim() || phoneSearch.length < 7) {
      setError('يجب إدخال رقم هاتف صحيح (7 أرقام على الأقل)');
      return;
    }

    setSearching(true);
    setError(null);
    setFoundCustomer(null);

    try {
      const { data: customers, error } = await supabase
        .from('profiles')
        .select('id, username, phone, country_code, smart_key, account_type')
        .eq('phone', phoneSearch.trim())
        .eq('country_code', selectedCountryCode);

      if (error) throw error;

      if (customers && customers.length > 0) {
        const customer = customers[0];
        if (customer.account_type === 'customer') {
          setFoundCustomer(customer);
          setCustomerSmartKey(customer.smart_key);
          setError(null);
        } else {
          setError('المستخدم الموجود ليس عميلاً');
        }
      } else {
        // Customer not found, prepare invitation
        setShowInvitationModal(true);
        setInvitationPhone(phoneSearch.trim());
        setInvitationCountryCode(selectedCountryCode);
        setInvitationAddress('');
      }
    } catch (err) {
      console.error('Error searching for customer:', err);
      setError('فشل في البحث عن العميل');
    } finally {
      setSearching(false);
    }
  };

  const handleSmartKeySearch = async (value: string) => {
    const searchValue = value.toUpperCase();
    setCustomerSmartKey(searchValue);
    setError(null);
    setFoundCustomer(null);
    
    if (searchValue.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('smart_key, username')
        .ilike('smart_key', `${searchValue}%`)
        .limit(5);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (err) {
      console.error('Error searching smart keys:', err);
      setError('Failed to search for customers');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleCreateAndShareInvitation = async () => {
    if (!invitationPhone.trim() || creatingInvitation) return;

    setCreatingInvitation(true);
    try {
      // Create invitation
      const { data: invitation, error } = await supabase
        .rpc('create_invitation', {
          p_customer_phone: invitationPhone.trim(),
          p_customer_country_code: invitationCountryCode,
          p_customer_address: invitationAddress.trim() || null
        });

      if (error) throw error;

      const invitationUrl = invitation.invitation_url;
      
      console.log('Invitation created:', invitation);
      console.log('Invitation URL:', invitationUrl);
      
      const whatsappMessage = `مرحباً! 👋

${vendorProfile?.username} من ${vendorProfile?.shop_name || 'متجره'} يدعوك للانضمام إلى تطبيق آجل لإدارة الحسابات.

📱 سجل الآن واستمتع بميزات:
• متابعة حسابك بسهولة
• استلام إشعارات فورية
• طباعة كشوف الحساب

👆 انقر على الرابط للتسجيل:
${invitationUrl}

شكراً لك! 🙏

رمز الدعوة: ${invitation.code}`;

      const whatsappUrl = `https://wa.me/${invitationCountryCode.replace('+', '')}${invitationPhone}?text=${encodeURIComponent(whatsappMessage)}`;
      
      console.log('WhatsApp URL:', whatsappUrl);
      window.open(whatsappUrl, '_blank');
      setShowInvitationModal(false);
      setPhoneSearch('');
      setInvitationPhone('');
      setInvitationAddress('');
    } catch (err) {
      console.error('Error creating invitation:', err);
      setError(err instanceof Error ? err.message : 'فشل في إنشاء الدعوة');
    } finally {
      setCreatingInvitation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const { data, error } = await supabase
        .rpc('create_vendor_customer_relationship', {
          p_customer_smart_key: customerSmartKey.trim().toUpperCase()
        });

      if (error) throw error;

      setSuccess(true);
      setCustomerSmartKey('');
      setSearchResults([]);
      setShowQrScanner(false);
      setFoundCustomer(null);
      setPhoneSearch('');
    } catch (err) {
      console.error('Error adding customer:', err);
      setError(err instanceof Error ? err.message : 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  const handleQrScan = (result: { text: string } | null) => {
    if (result?.text) {
      setCustomerSmartKey(result.text.toUpperCase());
      setShowQrScanner(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 p-6">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-8">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-white/50"
        >
          <ArrowRight size={24} className="text-gray-800" />
        </button>
        <div className="flex items-center space-x-2">
          <h1 className="text-xl font-semibold">إضافة عميل</h1>
          <Tooltip text="إضافة عميل جديد لمتجرك. يمكنك البحث بالهاتف أو استخدام الرمز الذكي." />
        </div>
      </div>

      <div className="w-full max-w-sm mx-auto bg-white rounded-3xl shadow-lg p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">عميل جديد</h2>
          <p className="text-gray-600">ابحث بالهاتف أو استخدم الرمز الذكي</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 rounded-xl flex items-center space-x-2 text-red-600">
            <AlertCircle size={20} />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 rounded-xl text-green-600 text-sm">
            تمت إضافة العميل بنجاح!
          </div>
        )}

        {/* Phone Search Section */}
        <div className="mb-6 p-4 bg-blue-50 rounded-xl">
          <div className="flex items-center space-x-2 mb-4">
            <Phone size={20} className="text-blue-600" />
            <h3 className="font-medium text-blue-800">البحث برقم الهاتف</h3>
          </div>
          
          <div className="space-y-3">
            <div>
              <select
                value={selectedCountryCode}
                onChange={(e) => setSelectedCountryCode(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-blue-200 focus:outline-none focus:border-blue-500 bg-white text-sm"
              >
                {countryCodes.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.country} ({country.code})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex space-x-2">
              <input
                type="tel"
                placeholder="رقم الهاتف"
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg border border-blue-200 focus:outline-none focus:border-blue-500 text-right text-sm"
              />
              <button
                type="button"
                onClick={handlePhoneSearch}
                disabled={searching || !phoneSearch.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {searching ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Search size={16} />
                )}
              </button>
            </div>
          </div>

          {foundCustomer && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-2 text-green-700">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">تم العثور على العميل!</span>
              </div>
              <p className="text-sm text-green-600 mt-1">
                {foundCustomer.username} - {foundCustomer.smart_key}
              </p>
            </div>
          )}
        </div>

        {/* Smart Key Section */}
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="relative">
            <div className="flex space-x-2">
              <input
                type="text"
                placeholder="الرمز الذكي للعميل"
                value={customerSmartKey}
                onChange={(e) => handleSmartKeySearch(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-blue-500 uppercase"
                maxLength={6}
                pattern="[A-Z0-9]{6}"
                title="يجب أن يكون الرمز الذكي 6 أحرف"
                required
              />
              <button
                type="button"
                onClick={() => setShowQrScanner(!showQrScanner)}
                className="px-4 py-3 bg-blue-100 text-blue-600 rounded-xl hover:bg-blue-200 transition-colors"
              >
                <QrCode size={20} />
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              أدخل الرمز الذكي المكون من 6 أحرف أو ابحث بالهاتف أعلاه
            </p>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={result.smart_key}
                    type="button"
                    onClick={() => {
                      setCustomerSmartKey(result.smart_key);
                      setSearchResults([]);
                    }}
                    className="w-full px-4 py-3 text-right hover:bg-gray-50 flex justify-between items-center"
                  >
                    <span className="font-medium">{result.smart_key}</span>
                    <span className="text-sm text-gray-500">{result.username}</span>
                  </button>
                ))}
              </div>
            )}

            {searching && !foundCustomer && (
              <div className="absolute right-3 top-3">
                <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>

          {showQrScanner && (
            <div className="relative">
              <div className="absolute -top-2 right-2 z-10">
                <button
                  type="button"
                  onClick={() => setShowQrScanner(false)}
                  className="p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="rounded-xl overflow-hidden">
                <QrReader
                  onError={(err) => console.error(err)}
                  onScan={handleQrScan}
                  style={{ width: '100%' }}
                  facingMode="environment"
                  delay={1000}
                  constraints={{
                    video: { facingMode: "environment" }
                  }}
                />
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !customerSmartKey || customerSmartKey.length !== 6}
            className="w-full py-3 bg-[#F87171] text-white rounded-xl hover:bg-[#EF4444] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'جاري إضافة العميل...' : 'إضافة العميل'}
          </button>
        </form>
      </div>

      {/* Invitation Modal */}
      {showInvitationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-md rounded-2xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">دعوة عميل جديد</h3>
              <button 
                onClick={() => {
                  setShowInvitationModal(false);
                  setError(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center space-x-2 text-orange-700 mb-2">
                <AlertCircle size={16} />
                <span className="text-sm font-medium">لم يتم العثور على العميل</span>
              </div>
              <p className="text-sm text-orange-600">
                رقم الهاتف: {invitationCountryCode} {invitationPhone}
              </p>
              <p className="text-sm text-orange-600">
                يمكنك إنشاء رابط دعوة قصير وآمن للتسجيل في التطبيق
              </p>
            </div>

            {/* Pre-filled Information Display */}
            <div className="space-y-4 mb-6">
              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">رقم الهاتف</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={invitationCountryCode}
                    disabled
                    className="w-20 px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm text-center"
                  />
                  <input
                    type="tel"
                    value={invitationPhone}
                    disabled
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">العنوان (اختياري)</label>
                <input
                  type="text"
                  placeholder="يمكن تركه فارغ ليملأه العميل"
                  value={invitationAddress}
                  onChange={(e) => setInvitationAddress(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-blue-500 text-sm"
                />
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-1">نوع الحساب</label>
                <input
                  type="text"
                  value="عميل"
                  disabled
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm"
                />
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">ملاحظة:</span> سيتم إنشاء رابط دعوة آمن ومؤقت (صالح لمدة 48 ساعة) وسيتم ربط العميل بحسابك تلقائياً عند التسجيل
                </p>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowInvitationModal(false);
                  setError(null);
                }}
                disabled={creatingInvitation}
                className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
              >
                إلغاء
              </button>
              <button
                type="button"
                onClick={handleCreateAndShareInvitation}
                disabled={creatingInvitation || !invitationPhone.trim()}
                className="flex-1 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
              >
                <Share2 size={18} />
                <span>{creatingInvitation ? 'جاري الإنشاء...' : 'إنشاء ومشاركة الدعوة'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddCustomer;